import * as React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { AllowListItem } from 'src/features/allowList/utils/types';
import { allowListMessages } from 'src/features/allowList/utils/allowListMessages';
import { PERMISSION } from 'src/features/auth/types';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { DataTable } from 'src/registry/components/table/DataTable';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import { DataTableControls } from 'src/registry/components/table/DataTableControls';
import { DataTablePagination } from 'src/registry/components/table/DataTablePagination';
import useFilters from 'src/registry/hooks/useFilters';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import { generalMessages } from 'src/registry/utils/messages';
import { useListAllowListItems } from 'src/features/allowListItem/api/useListAllowListItems';
import { CellBadge } from 'src/registry/components/table';
import CreateAllowListItemFormModal from 'src/features/allowListItem/components/forms/CreateAllowListItemFormModal';
import CellAllowListItemActions from 'src/features/allowListItem/components/CellAllowListItemActions';
import { identifiersMessages } from 'src/features/membership/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import { allowListIdentifierTypeBadgeOptions } from 'src/features/allowList/utils/constants';

const sortingOptions = {
    initialSortingState: [{ id: 'name', desc: false }],
};
const searchOptions = { globalFilterKey: 'search' };

const columnHelper = createColumnHelper<AllowListItem>();

type Props = {
    listId: string;
};

export default function AllowListItems({ listId }: Props) {
    const { formatMessage } = useIntl();

    const manageAllowLists = useHasPermissions([PERMISSION.MANAGE_ALLOW_LISTS]);

    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySearchOptions, ...search } = useSearch(searchOptions);
    const { querySortingOptions, ...sorting } = useSorting(sortingOptions);
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { parkingId } = useParking();
    const payload = React.useMemo(
        () => ({
            ...queryFiltersOptions,
            ...querySearchOptions,
            ...querySortingOptions,
            ...queryPaginationOptions,
            listId,
            parkingId,
        }),
        [parkingId, listId, queryFiltersOptions, querySearchOptions, querySortingOptions, queryPaginationOptions],
    );

    const { data, isLoading } = useListAllowListItems(payload);

    const columns = [
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            enableSorting: true,
            meta: {
                title: formatMessage(generalMessages.name),
            },
        }),
        columnHelper.accessor('identifier_value', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(identifiersMessages.identifier),
            },
        }),
        columnHelper.accessor('identifier_type', {
            enableSorting: true,
            enableColumnFilter: true,
            enableHiding: true,
            header: DataTableColumnHeader,
            cell: CellBadge,
            meta: {
                title: formatMessage(identifiersMessages.identifierType),
                badgeOptions: allowListIdentifierTypeBadgeOptions,
            },
        }),
        columnHelper.display({
            id: 'actions',
            header: DataTableColumnHeader,
            cell: CellAllowListItemActions,
            maxSize: 45,
        }),
    ];

    return (
        <DataTable<AllowListItem, any>
            isLoading={isLoading}
            columns={columns}
            data={data}
            search={search}
            filters={filters}
            sorting={sorting}
            pagination={pagination}
            semantics={{
                title: formatMessage(allowListMessages.allowListItems),
                entryName: formatMessage(allowListMessages.allowListItem).toLowerCase(),
                entryNames: formatMessage(allowListMessages.allowListItems).toLowerCase(),
                placeholder: formatMessage(allowListMessages.allowListItem),
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(manageAllowLists && { newEntry: CreateAllowListItemFormModal }),
            }}
        />
    );
}
